import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { LoadingSpinner } from '@/components/common';
import { formatCurrency, formatDate } from '@/utils';

/**
 * The shareable POLICY link -- the other half of `PublicQuote`.
 *
 * A broker could send someone a quote and then had no way to send them the
 * policy it became. `PublicPolicyViewSet` serves `/api/public/policies/<token>/`
 * for a policy the broker has explicitly shared.
 *
 * Unauthenticated by design, so it uses bare `axios` against `/policy/api/public/`
 * rather than the `@/services/api` instance, which would attach a bearer token
 * and run the refresh interceptor. Same approach as PublicQuote.
 *
 * The payload is `PublicPolicySerializer`, an explicit allowlist. Anyone
 * holding this link can read this page, so a field's ABSENCE from the type
 * below is a security decision, not an oversight -- no commission of any kind,
 * no contact details, no documents, no internal identifiers. Adding one here
 * means adding it there first, deliberately.
 */

interface PublicPolicy {
  /**
   * Global #7: the INSURER's number is the only one this page shows. Our
   * internal `policy_number` (`POL-22-godigit-260722-9A1AD5`) is no longer in
   * the payload at all -- it was rendered under a "Policy Number" label, so a
   * customer quoting it to their insurer was reading out an identifier the
   * insurer has never issued. When the insurer has given none the page says
   * so with an em dash; it does not substitute ours.
   */
  insurer_policy_number?: string | null;
  insurer_name: string;
  broker_name: string;
  product_name?: string | null;
  insurance_type_name?: string | null;
  status: string;
  holder_name: string;
  inception_date: string;
  expiry_date: string;
  issue_date?: string | null;
  policy_term_months?: number | null;
  vehicle_number?: string | null;
  vehicle_make_name?: string | null;
  vehicle_model_name?: string | null;
  vehicle_variant_name?: string | null;
  registration_date?: string | null;
  manufacturing_year?: number | null;
  idv?: string | null;
  sum_insured?: string | null;
  ncb_percentage?: string | number | null;
  od_premium?: string | null;
  tp_premium?: string | null;
  net_premium?: string | null;
  total_premium?: string | null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  // An empty row is not shown at all. A public page listing "Vehicle: -" for a
  // health policy reads as missing data on a complete record.
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right break-words">
        {value}
      </dd>
    </div>
  );
}

/** Colour for the lifecycle pill. An unknown label falls back to neutral. */
// eslint-disable-next-line react-refresh/only-export-components
export function publicStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'upcoming':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'cancelled':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="px-6 py-5 border-t border-gray-100 dark:border-gray-800">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
        {title}
      </h2>
      <dl className="divide-y divide-gray-100 dark:divide-gray-800">{children}</dl>
    </section>
  );
}

export function PublicPolicy() {
  const { token } = useParams<{ token: string }>();
  const [policy, setPolicy] = useState<PublicPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/policy/api/public/policies/${token}/`);
        setPolicy(response.data);
      } catch {
        // The backend drops unshared, draft, cancelled and deleted policies
        // from this queryset, so a link legitimately stops working when the
        // broker withdraws it. From the recipient's side that is the same
        // thing as a bad token, and the page must not say which.
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          This policy link is no longer available
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          The link may have been withdrawn, or the policy may no longer be active. Please
          contact your broker for an up-to-date copy.
        </p>
      </div>
    );
  }

  const hasVehicle = !!(
    policy.vehicle_number ||
    policy.vehicle_make_name ||
    policy.vehicle_model_name
  );
  const vehicleName = [policy.vehicle_make_name, policy.vehicle_model_name, policy.vehicle_variant_name]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4">
      <div className="mx-auto max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <header className="px-6 py-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">{policy.insurer_name}</p>
          <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white break-words">
            {policy.insurer_policy_number || 'Policy details'}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {policy.holder_name}
            {policy.product_name ? ` · ${policy.product_name}` : ''}
          </p>
          {/* B9: `status` is now the computed lifecycle label (Upcoming /
              Active / Expired / Cancelled), blank for a policy with no
              lifecycle. A blank one renders no pill at all rather than an
              empty bubble. */}
          {policy.status && (
            <span
              data-testid="public-policy-status"
              className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-medium ${publicStatusClass(policy.status)}`}
            >
              {policy.status}
            </span>
          )}
        </header>

        <Section title="Cover">
          {/* Always rendered, em dash and all: a missing row would read as
              "this policy has no such field", and the holder needs to see
              that the insurer's number is the one thing not on record. */}
          <Row label="Policy Number" value={policy.insurer_policy_number || '—'} />
          <Row label="Type" value={policy.insurance_type_name} />
          <Row label="Starts" value={formatDate(policy.inception_date)} />
          <Row label="Ends" value={formatDate(policy.expiry_date)} />
          <Row label="Issued" value={policy.issue_date ? formatDate(policy.issue_date) : ''} />
          <Row
            label="Term"
            value={policy.policy_term_months ? `${policy.policy_term_months} months` : ''}
          />
          <Row label="Sum Insured" value={policy.sum_insured ? formatCurrency(policy.sum_insured) : ''} />
        </Section>

        {hasVehicle && (
          <Section title="Vehicle">
            <Row label="Registration" value={policy.vehicle_number} />
            <Row label="Vehicle" value={vehicleName} />
            <Row
              label="Registered"
              value={policy.registration_date ? formatDate(policy.registration_date) : ''}
            />
            <Row label="Year" value={policy.manufacturing_year} />
            <Row label="IDV" value={policy.idv ? formatCurrency(policy.idv) : ''} />
            <Row
              label="No Claim Bonus"
              value={policy.ncb_percentage ? `${policy.ncb_percentage}%` : ''}
            />
          </Section>
        )}

        <Section title="Premium">
          <Row label="Own Damage" value={policy.od_premium ? formatCurrency(policy.od_premium) : ''} />
          <Row label="Third Party" value={policy.tp_premium ? formatCurrency(policy.tp_premium) : ''} />
          <Row label="Net Premium" value={policy.net_premium ? formatCurrency(policy.net_premium) : ''} />
          <Row
            label="Total Premium"
            value={policy.total_premium ? formatCurrency(policy.total_premium) : ''}
          />
        </Section>

        <footer className="px-6 py-5 border-t border-gray-100 dark:border-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Arranged by {policy.broker_name}. Contact them for a copy of your policy documents
            or any change to this cover.
          </p>
        </footer>
      </div>
    </div>
  );
}
